import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaCalendarAlt, FaClock, FaCoins, FaUser } from 'react-icons/fa';
import { MdOutlineViewDay } from 'react-icons/md';
import { PiVideoConferenceFill } from 'react-icons/pi';
import { TbTransfer } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { useAppointmentListContext } from '../../../contexts/AppointmentListProvider';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { AppointmentRequest } from '../../../types/appointmentRequests';
import { showPicture } from '../../../utils/commonFunctions';
import {
  calculateTimeSlotDurationInMinutes,
  isUpcomingTime,
  showTimeIn12HrsFormat,
} from '../../../utils/timeAndDate';
import AcademiaCardBadge from '../AcademiaCardBadge';
import AcademiaLiveBadge from '../AcademiaLiveBadge';
import AcademiaTimer from '../AcademiaTimer';
import SkillBadge from '../Badges/SkillBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AppointmentDetailsModal from '../Modals/AppointmentDetailsModal';
import AppointmentReferralDetailsModal from '../Modals/AppointmentReferralDetailsModal';
import CreateEditReferralModal from '../Modals/CreateEditReferralModal';

const AppointmentCard = ({
  appointment,
  isUpcoming,
  isLive,
  isUnattended,
  isAssociate,
}: {
  appointment: AppointmentRequest;
  isUpcoming?: boolean;
  isLive?: boolean;
  isUnattended?: boolean;
  isAssociate?: boolean;
}) => {
  // destructuring the appointment object here
  const {
    associate_profile_picture,
    client_profile_picture,
    client_full_name,
    associate_full_name,
    credits_cost,
    date,
    start_time,
    end_time,
    slot_duration,
    appointment_buffer_time,
    room_id,
    is_complete,
    service_name,
  } = appointment || {};

  // integration of react hooks here
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showReferModal, setShowReferModal] = useState<boolean>(false);
  const [isTimeUp, setIsTimeUp] = useState<boolean>(false);

  // integration of context hooks here
  const { currentUser, currentUserType } = useAuthContext();
  const { appointmentType } = useAppointmentListContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // integration of react router dom hooks here
  const navigate = useNavigate();

  // refetching appointments when any appointment goes live here
  useEffect(() => {
    if (isTimeUp && currentUser?.id) {
      if (currentUserType === 'user') {
        queryClient.invalidateQueries({
          queryKey: [
            'appointment-by-associate-id',
            currentUser.id,
            appointmentType,
          ],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [
            'appointment-by-client-id',
            currentUser.id,
            appointmentType,
          ],
        });
      }
    }
  }, [isTimeUp, currentUserType, currentUser, appointmentType, queryClient]);

  // calculating total duration in minutes for calculating number of time slots here
  const totalDuration = calculateTimeSlotDurationInMinutes(
    start_time,
    end_time
  );

  // rendering appointment card component here
  return (
    <div className='grid grid-rows-subgrid row-span-3 bg-base-white p-4 rounded-lg shadow-academia-sm'>
      <div>
        {isLive ? (
          <AcademiaLiveBadge />
        ) : isUpcoming ? (
          <AcademiaTimer
            targetDate={date}
            targetStartTime={start_time}
            setIsTimeUp={setIsTimeUp}
          />
        ) : appointmentType === 'all' && is_complete ? (
          <AcademiaCardBadge
            badgeTxt='completed'
            additionalClassNames='text-base-purple border-base-purple'
          />
        ) : appointmentType === 'all' && isUnattended ? (
          <AcademiaCardBadge
            badgeTxt='unattended'
            additionalClassNames='text-base-orange border-base-orange'
          />
        ) : null}
      </div>

      <figure className='flex items-center justify-center'>
        {isAssociate ? (
          client_profile_picture ? (
            <img
              className='size-24 rounded-full object-cover'
              src={showPicture(client_profile_picture)}
              alt={`avatar of ${client_full_name}`}
            />
          ) : (
            <div className='flex items-center justify-center bg-base-light-gray size-24 rounded-full'>
              <FaUser className='size-14 text-gray-300' />
            </div>
          )
        ) : associate_profile_picture ? (
          <img
            className='size-24 rounded-full object-cover'
            src={showPicture(associate_profile_picture)}
            alt={`avatar of ${associate_full_name}`}
          />
        ) : (
          <div className='flex items-center justify-center bg-base-light-gray size-24 rounded-full'>
            <FaUser className='size-14 text-gray-300' />
          </div>
        )}
      </figure>

      <figcaption className='space-y-2'>
        <h3 className='text-center text-lg text-base-dark-blue font-semibold'>
          {isAssociate ? client_full_name : associate_full_name}
        </h3>

        <div className='flex items-center justify-center gap-1 font-medium text-base-orange text-lg'>
          <FaCoins className='size-4' />
          <p>{credits_cost || 0} Credits</p>
        </div>

        <div className='flex flex-wrap items-center justify-between'>
          <SkillBadge badgeLabel={service_name} color='#388229' />
          <SkillBadge
            badgeLabel={`${
              Math.ceil(
                totalDuration / (slot_duration + appointment_buffer_time)
              ) || 1
            }
          ${
            Math.ceil(
              totalDuration / (slot_duration + appointment_buffer_time)
            ) > 1
              ? 'slots'
              : 'slot'
          }`}
            color='#1ABC9C'
          />
        </div>

        <div className='flex items-center justify-between px-1 text-sm font-semibold text-base-purple'>
          <div className='flex items-center gap-1'>
            <FaCalendarAlt className='size-5' />
            <p>{moment(date).format('DD-MM-YYYY')}</p>
          </div>

          <div className='flex items-center gap-1'>
            <FaClock className='size-4' />
            <p>
              {`${showTimeIn12HrsFormat(start_time)} - ${showTimeIn12HrsFormat(
                end_time
              )}`}
            </p>
          </div>
        </div>

        <div className='space-y-2 !mt-3'>
          {isLive ||
          isUpcoming ||
          (is_complete && currentUserType === 'client') ||
          !is_complete ? (
            <AcademiaButton
              title={
                isUpcomingTime(date, start_time)
                  ? 'Session has not started yet!'
                  : !isUpcomingTime(date, start_time) && !isLive
                  ? 'Session time is up!'
                  : undefined
              }
              type='button'
              onClick={() => navigate(`/session/${room_id}`)}
              additionalClassNames='!py-2'
              icon={<PiVideoConferenceFill className='size-6' />}
              isDisabled={
                isUpcomingTime(date, start_time) ||
                (!isUpcomingTime(date, start_time) && !isLive)
              }
              variant='premium'
              fullWidth
            >
              Join Session
            </AcademiaButton>
          ) : null}

          <AcademiaButton
            type='button'
            onClick={() => setShowDetailsModal(true)}
            additionalClassNames='!py-2'
            icon={<MdOutlineViewDay className='size-6' />}
            variant='info'
            fullWidth
          >
            View {appointmentType === 'referrals' ? 'Referrals' : 'Details'}
          </AcademiaButton>

          {is_complete && currentUserType !== 'client' ? (
            <AcademiaButton
              type='button'
              onClick={() => setShowReferModal(true)}
              additionalClassNames='!py-2'
              icon={<TbTransfer className='size-6' />}
              variant='primary'
              fullWidth
            >
              Refer Client
            </AcademiaButton>
          ) : null}
        </div>
      </figcaption>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDetailsModal ? (
          appointmentType === 'referrals' ? (
            <AppointmentReferralDetailsModal
              referrals={appointment.referred_to}
              handleModalClose={() => setShowDetailsModal(false)}
            />
          ) : (
            <AppointmentDetailsModal
              appointment={appointment}
              handleModalClose={() => setShowDetailsModal(false)}
            />
          )
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showReferModal ? (
          <CreateEditReferralModal
            title='create'
            appointment={appointment}
            handleModalClose={() => setShowReferModal(false)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default AppointmentCard;
